import { Guid } from "@/Common/Models/Guid";
import { Flowsheets as FlowsheetActions } from "@/Common/Operations/operations.patient.local";
import { LocalEvent } from "@/Common/Stores/Actions/LocalEvent";
import { BaseOperationHandler } from "@/Common/Stores/Handlers/Local/BaseOperationHandler";
import flowsheetToFlowsheetSummary from "@/Flowsheets/Mappers/FlowsheetToFlowsheetSummary";
import { Flowsheet } from "@/Flowsheets/Models/Flowsheet";
import { FlowsheetSummary } from "@/Flowsheets/Models/FlowsheetSummary";
import { FlowsheetTemplate } from "@/Flowsheets/Models/FlowsheetTemplate";
import { IFlowsheetModel } from "@/Patient/Models/JSONInterfaces/Flowsheet/IFlowsheetModel";
import { PatientStore } from "@/Patient/Stores/PatientStore";

class PatientFlowsheetsHandler extends BaseOperationHandler {
    constructor() {
        super();

        this.RegisterOperation(FlowsheetActions.AddFlowsheetSummary, this.addFlowsheetSummary);
        this.RegisterOperation(FlowsheetActions.SaveFlowsheet, this.saveFlowsheet);
        this.RegisterOperation(FlowsheetActions.DeleteFlowsheet, this.deleteFlowsheet);
        this.RegisterOperation(FlowsheetActions.SetForceReload, this.setForceReload);
    }

    public addFlowsheetSummary(request: LocalEvent, store: PatientStore): void {
        const flowsheetSummary = request.Get<FlowsheetSummary>("flowsheet");
        const patientId = request.PatientId;

        store.FlowsheetModels[patientId] ??= [];

        const index = store.FlowsheetModels[patientId].findIndex(p => p.get("flowsheetId") === flowsheetSummary.Id);

        if (index !== -1) {
            store.FlowsheetModels[patientId][index] = store.FlowsheetModels[patientId][index].set("flowsheetSummary", flowsheetSummary);
            return;
        }

        const model = Immutable.fromJS({
            encounterId: flowsheetSummary.EncounterId,
            flowsheetId: flowsheetSummary.Id,
            flowsheetSummary
        }) as IFlowsheetModel;
        store.FlowsheetModels[patientId].push(model);
    }

    public saveFlowsheet(request: LocalEvent, store: PatientStore): void {
        const flowsheet = request.Get<Flowsheet>("flowsheet");
        const patientId = request.PatientId;
        const encounterId = flowsheet.EncounterId;
        const template = request.Get<FlowsheetTemplate>("template");

        store.FlowsheetModels[patientId] ??= [];
        const index = store.FlowsheetModels[patientId].findIndex(p => p.get("flowsheetId") === flowsheet.Id);
        let model = store.FlowsheetModels[patientId][index];

        if (!model) {
            model = Immutable.fromJS({ encounterId, flowsheetId: flowsheet.Id, flowsheet }) as IFlowsheetModel;
        }
        else {
            model = model.set("flowsheet", flowsheet);
        }

        if (template) {
            model = model
                .set("flowsheetSummary", flowsheetToFlowsheetSummary(flowsheet, template));
        }

        if (index === -1) {
            store.FlowsheetModels[patientId].push(model);
        }
        else {
            store.FlowsheetModels[patientId][index] = model;
        }
    }

    public deleteFlowsheet(request: LocalEvent, store: PatientStore): void {
        const flowsheetId = request.Get<Guid>("flowsheetId");
        const patientId = request.PatientId;

        if (!store.FlowsheetModels[patientId])
            return;

        store.FlowsheetModels[patientId] = store.FlowsheetModels[patientId].filter(
            p => p.get("flowsheetId") !== flowsheetId
        );
    }

    public setForceReload(request: LocalEvent, store: PatientStore): void {
        const value = request.Get<boolean>("value");
        const patientId = request.Get<string>("patientId") ?? request.PatientId;

        if (!store.FlowsheetModels[patientId])
            return;

        store.FlowsheetModels[patientId] = store.FlowsheetModels[patientId].map(flowsheet => flowsheet.set("forceReload", value));
    }
}

export {
    PatientFlowsheetsHandler
};
